import { Platform } from '@angular/cdk/platform';
import * as i0 from '@angular/core';
import { inject, NgZone, DestroyRef, ElementRef, Input, Directive, ChangeDetectionStrategy, ViewEncapsulation, Component, Renderer2, ViewContainerRef, signal, computed, booleanAttribute, ChangeDetectorRef, ContentChildren, forwardRef, numberAttribute, ViewChildren, isDevMode, ContentChild, NgModule } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NzResizeService } from 'ng-zorro-antd/core/services';
import * as i2 from 'ng-zorro-antd/core/outlet';
import { NzOutletModule } from 'ng-zorro-antd/core/outlet';
import * as i1 from 'ng-zorro-antd/icon';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { FocusMonitor } from '@angular/cdk/a11y';
import { Directionality } from '@angular/cdk/bidi';
import { NgTemplateOutlet } from '@angular/common';
import { Subject, merge, EMPTY } from 'rxjs';
import { distinctUntilChanged, filter, startWith, switchMap, mergeMap, map, tap } from 'rxjs/operators';
import { NzFormStatusService, NzFormNoStatusService, NzFormItemFeedbackIconComponent } from 'ng-zorro-antd/core/form';
import { getStatusClassNames, isNotNil } from 'ng-zorro-antd/core/util';
import * as i1$1 from 'ng-zorro-antd/space';
import { NZ_SPACE_COMPACT_SIZE, NZ_SPACE_COMPACT_ITEM_TYPE, NzSpaceCompactItemDirective } from 'ng-zorro-antd/space';
import * as i1$2 from '@angular/forms';
import { NgControl, FormBuilder, Validators, ReactiveFormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BACKSPACE } from '@angular/cdk/keycodes';

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
const _c0 = ["nz-input-group-slot", ""];
const _c1 = ["*"];
function NzInputGroupSlotComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "nz-icon", 0);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("nzType", ctx_r0.icon);
  }
}
function NzInputGroupSlotComponent_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r0.template);
  }
}
function NzInputGroupComponent_Conditional_0_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "span", 3);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("icon", ctx_r0.nzAddOnBeforeIcon)("template", ctx_r0.nzAddOnBefore);
  }
}
function NzInputGroupComponent_Conditional_0_Conditional_2_ng_template_1_Template(rf, ctx) {}
function NzInputGroupComponent_Conditional_0_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 6);
    i0.ɵɵtemplate(1, NzInputGroupComponent_Conditional_0_Conditional_2_ng_template_1_Template, 0, 0, "ng-template", 5);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    const affixTemplate_r2 = i0.ɵɵreference(3);
    i0.ɵɵclassMap(ctx_r0.affixInGroupStatusCls);
    i0.ɵɵclassProp("ant-input-affix-wrapper-disabled", ctx_r0.disabled)("ant-input-affix-wrapper-sm", ctx_r0.isSmall)("ant-input-affix-wrapper-lg", ctx_r0.isLarge)("ant-input-affix-wrapper-focused", ctx_r0.focused);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", affixTemplate_r2);
  }
}
function NzInputGroupComponent_Conditional_0_Conditional_3_ng_template_0_Template(rf, ctx) {}
function NzInputGroupComponent_Conditional_0_Conditional_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, NzInputGroupComponent_Conditional_0_Conditional_3_ng_template_0_Template, 0, 0, "ng-template", 5);
  }
  if (rf & 2) {
    i0.ɵɵnextContext(2);
    const contentTemplate_r3 = i0.ɵɵreference(5);
    i0.ɵɵproperty("ngTemplateOutlet", contentTemplate_r3);
  }
}
function NzInputGroupComponent_Conditional_0_Conditional_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "span", 3);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("icon", ctx_r0.nzAddOnAfterIcon)("template", ctx_r0.nzAddOnAfter);
  }
}
function NzInputGroupComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 2);
    i0.ɵɵconditionalCreate(1, NzInputGroupComponent_Conditional_0_Conditional_1_Template, 1, 2, "span", 3);
    i0.ɵɵconditionalCreate(2, NzInputGroupComponent_Conditional_0_Conditional_2_Template, 2, 11, "span", 4)(3, NzInputGroupComponent_Conditional_0_Conditional_3_Template, 1, 1, null, 5);
    i0.ɵɵconditionalCreate(4, NzInputGroupComponent_Conditional_0_Conditional_4_Template, 1, 2, "span", 3);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r0.nzAddOnBefore || ctx_r0.nzAddOnBeforeIcon ? 1 : -1);
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r0.isAffix || ctx_r0.hasFeedback ? 2 : 3);
    i0.ɵɵadvance(2);
    i0.ɵɵconditional(ctx_r0.nzAddOnAfter || ctx_r0.nzAddOnAfterIcon ? 4 : -1);
  }
}
function NzInputGroupComponent_Conditional_1_Conditional_0_ng_template_0_Template(rf, ctx) {}
function NzInputGroupComponent_Conditional_1_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, NzInputGroupComponent_Conditional_1_Conditional_0_ng_template_0_Template, 0, 0, "ng-template", 5);
  }
  if (rf & 2) {
    i0.ɵɵnextContext(2);
    const affixTemplate_r2 = i0.ɵɵreference(3);
    i0.ɵɵproperty("ngTemplateOutlet", affixTemplate_r2);
  }
}
function NzInputGroupComponent_Conditional_1_Conditional_1_ng_template_0_Template(rf, ctx) {}
function NzInputGroupComponent_Conditional_1_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, NzInputGroupComponent_Conditional_1_Conditional_1_ng_template_0_Template, 0, 0, "ng-template", 5);
  }
  if (rf & 2) {
    i0.ɵɵnextContext(2);
    const contentTemplate_r3 = i0.ɵɵreference(5);
    i0.ɵɵproperty("ngTemplateOutlet", contentTemplate_r3);
  }
}
function NzInputGroupComponent_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵconditionalCreate(0, NzInputGroupComponent_Conditional_1_Conditional_0_Template, 1, 1, null, 5)(1, NzInputGroupComponent_Conditional_1_Conditional_1_Template, 1, 1, null, 5);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵconditional(ctx_r0.isAffix ? 0 : 1);
  }
}
function NzInputGroupComponent_ng_template_2_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "span", 7);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("icon", ctx_r0.nzPrefixIcon)("template", ctx_r0.nzPrefix);
  }
}
function NzInputGroupComponent_ng_template_2_ng_template_1_Template(rf, ctx) {}
function NzInputGroupComponent_ng_template_2_Conditional_2_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "nz-form-item-feedback-icon", 9);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(3);
    i0.ɵɵproperty("status", ctx_r0.status);
  }
}
function NzInputGroupComponent_ng_template_2_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 8);
    i0.ɵɵconditionalCreate(1, NzInputGroupComponent_ng_template_2_Conditional_2_Conditional_1_Template, 1, 1, "nz-form-item-feedback-icon", 9);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("icon", ctx_r0.nzSuffixIcon)("template", ctx_r0.nzSuffix);
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r0.isFeedback ? 1 : -1);
  }
}
function NzInputGroupComponent_ng_template_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵconditionalCreate(0, NzInputGroupComponent_ng_template_2_Conditional_0_Template, 1, 2, "span", 7);
    i0.ɵɵtemplate(1, NzInputGroupComponent_ng_template_2_ng_template_1_Template, 0, 0, "ng-template", 5);
    i0.ɵɵconditionalCreate(2, NzInputGroupComponent_ng_template_2_Conditional_2_Template, 2, 3, "span", 8);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    const contentTemplate_r3 = i0.ɵɵreference(5);
    i0.ɵɵconditional(ctx_r0.nzPrefix || ctx_r0.nzPrefixIcon ? 0 : -1);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", contentTemplate_r3);
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r0.nzSuffix || ctx_r0.nzSuffixIcon || ctx_r0.isFeedback ? 2 : -1);
  }
}
function NzInputGroupComponent_ng_template_4_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 10);
    i0.ɵɵelement(1, "nz-form-item-feedback-icon", 9);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵproperty("status", ctx_r0.status);
  }
}
function NzInputGroupComponent_ng_template_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵprojection(0);
    i0.ɵɵconditionalCreate(1, NzInputGroupComponent_ng_template_4_Conditional_1_Template, 2, 1, "span", 10);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵconditional(!ctx_r0.isAddOn && !ctx_r0.isAffix && ctx_r0.isFeedback ? 1 : -1);
  }
}
const _c2 = ["otpInput"];
function NzInputOtpComponent_For_1_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "input", 2, 0);
    i0.ɵɵlistener("input", function NzInputOtpComponent_For_1_Template_input_input_0_listener($event) {
      const $index_r2 = i0.ɵɵrestoreView(_r1).$index;
      const ctx_r2 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r2.onInput($index_r2, $event));
    })("focus", function NzInputOtpComponent_For_1_Template_input_focus_0_listener($event) {
      i0.ɵɵrestoreView(_r1);
      const ctx_r2 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r2.onFocus($event));
    })("keydown", function NzInputOtpComponent_For_1_Template_input_keydown_0_listener($event) {
      const $index_r2 = i0.ɵɵrestoreView(_r1).$index;
      const ctx_r2 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r2.onKeyDown($index_r2, $event));
    })("paste", function NzInputOtpComponent_For_1_Template_input_paste_0_listener($event) {
      const $index_r2 = i0.ɵɵrestoreView(_r1).$index;
      const ctx_r2 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r2.onPaste($index_r2, $event));
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const item_r4 = ctx.$implicit;
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵproperty("nzSize", ctx_r2.nzSize)("formControl", item_r4)("nzStatus", ctx_r2.nzStatus);
  }
}
const _c3 = [[["textarea", "nz-input", ""]]];
const _c4 = ["textarea[nz-input]"];
let NzAutosizeDirective = /*#__PURE__*/(() => {
  class NzAutosizeDirective {
    ngZone = inject(NgZone);
    platform = inject(Platform);
    destroyRef = inject(DestroyRef);
    resizeService = inject(NzResizeService);
    el = inject(ElementRef).nativeElement;
    autosize = false;
    cachedLineHeight;
    previousValue;
    previousMinRows;
    minRows;
    maxRows;
    maxHeight = null;
    minHeight = null;
    inputGap = 10;
    destroyed = false;
    constructor() {
      this.destroyRef.onDestroy(() => {
        this.destroyed = true;
      });
    }
    set nzAutosize(value) {
      const isAutoSizeType = data => typeof data !== 'string' && typeof data !== 'boolean' && (!!data.maxRows || !!data.minRows);
      if (typeof value === 'string' || value === true) {
        this.autosize = true;
      } else if (isAutoSizeType(value)) {
        this.autosize = true;
        this.minRows = value.minRows;
        this.maxRows = value.maxRows;
        this.maxHeight = this.setMaxHeight();
        this.minHeight = this.setMinHeight();
      }
    }
    resizeToFitContent(force = false) {
      this.cacheTextareaLineHeight();
      // If we haven't determined the line-height yet, we know we're still hidden and there's no point
      // in checking the height of the textarea.
      if (!this.cachedLineHeight) {
        return;
      }
      const textarea = this.el;
      const value = textarea.value;
      // Only resize if the value or minRows have changed since these calculations can be expensive.
      if (!force && this.minRows === this.previousMinRows && value === this.previousValue) {
        return;
      }
      const placeholderText = textarea.placeholder;
      // Reset the textarea height to auto in order to shrink back to its default size.
      // Also temporarily force overflow:hidden, so scroll bars do not interfere with calculations.
      // Long placeholders that are wider than the textarea width may lead to a bigger scrollHeight
      // value. To ensure that the scrollHeight is not bigger than the content, the placeholders
      // need to be removed temporarily.
      textarea.classList.add('nz-textarea-autosize-measuring');
      textarea.placeholder = '';
      let height = Math.round((textarea.scrollHeight - this.inputGap) / this.cachedLineHeight) * this.cachedLineHeight + this.inputGap;
      if (this.maxHeight !== null && height > this.maxHeight) {
        height = this.maxHeight;
      }
      if (this.minHeight !== null && height < this.minHeight) {
        height = this.minHeight;
      }
      // Use the scrollHeight to know how large the textarea *would* be if fit its entire value.
      textarea.style.height = `${height}px`;
      textarea.classList.remove('nz-textarea-autosize-measuring');
      textarea.placeholder = placeholderText;
      // On Firefox resizing the textarea will prevent it from scrolling to the caret position.
      // We need to re-set the selection in order for it to scroll to the proper position.
      if (typeof requestAnimationFrame !== 'undefined') {
        this.ngZone.runOutsideAngular(() => requestAnimationFrame(() => {
          const {
            selectionStart,
            selectionEnd
          } = textarea;
          // IE will throw an "Unspecified error" if we try to set the selection range after the
          // element has been removed from the DOM. Assert that the directive hasn't been destroyed
          // between the time we requested the animation frame and when it was executed.
          // Also note that we have to assert that the textarea is focused before we set the
          // selection range. Setting the selection range on a non-focused textarea will cause
          // it to receive focus on IE and Edge.
          if (!this.destroyed && document.activeElement === textarea) {
            textarea.setSelectionRange(selectionStart, selectionEnd);
          }
        }));
      }
      this.previousValue = value;
      this.previousMinRows = this.minRows;
    }
    cacheTextareaLineHeight() {
      if (this.cachedLineHeight >= 0 || !this.el.parentNode) {
        return;
      }
      // Use a clone element because we have to override some styles.
      const textareaClone = this.el.cloneNode(false);
      textareaClone.rows = 1;
      // Use `position: absolute` so that this doesn't cause a browser layout and use
      // `visibility: hidden` so that nothing is rendered. Clear any other styles that
      // would affect the height.
      textareaClone.style.position = 'absolute';
      textareaClone.style.visibility = 'hidden';
      textareaClone.style.border = 'none';
      textareaClone.style.padding = '0';
      textareaClone.style.height = '';
      textareaClone.style.minHeight = '';
      textareaClone.style.maxHeight = '';
      // In Firefox it happens that textarea elements are always bigger than the specified amount
      // of rows. This is because Firefox tries to add extra space for the horizontal scrollbar.
      // As a workaround that removes the extra space for the scrollbar, we can just set overflow
      // to hidden. This ensures that there is no invalid calculation of the line height.
      // See Firefox bug report: https://bugzilla.mozilla.org/show_bug.cgi?id=33654
      textareaClone.style.overflow = 'hidden';
      this.el.parentNode.appendChild(textareaClone);
      this.cachedLineHeight = textareaClone.clientHeight - this.inputGap;
      this.el.parentNode.removeChild(textareaClone);
      // Min and max heights have to be re-calculated if the cached line height changes
      this.maxHeight = this.setMaxHeight();
      this.minHeight = this.setMinHeight();
    }
    setMinHeight() {
      const minHeight = this.minRows && this.cachedLineHeight ? this.minRows * this.cachedLineHeight + this.inputGap : null;
      if (minHeight !== null) {
        this.el.style.minHeight = `${minHeight}px`;
      }
      return minHeight;
    }
    setMaxHeight() {
      const maxHeight = this.maxRows && this.cachedLineHeight ? this.maxRows * this.cachedLineHeight + this.inputGap : null;
      if (maxHeight !== null) {
        this.el.style.maxHeight = `${maxHeight}px`;
      }
      return maxHeight;
    }
    noopInputHandler() {
      // no-op handler that ensures we're running change detection on input events.
    }
    ngAfterViewInit() {
      if (this.autosize && this.platform.isBrowser) {
        this.resizeToFitContent();
        this.resizeService.connect().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.resizeToFitContent(true));
      }
    }
    ngDoCheck() {
      if (this.autosize && this.platform.isBrowser) {
        this.resizeToFitContent();
      }
    }
    static ɵfac = function NzAutosizeDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzAutosizeDirective)();
    };
    static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NzAutosizeDirective,
      selectors: [["textarea", "nzAutosize", ""]],
      hostAttrs: ["rows", "1"],
      hostBindings: function NzAutosizeDirective_HostBindings(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵlistener("input", function NzAutosizeDirective_input_HostBindingHandler() {
            return ctx.noopInputHandler();
          });
        }
      },
      inputs: {
        nzAutosize: "nzAutosize"
      },
      exportAs: ["nzAutosize"]
    });
  }
  return NzAutosizeDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzInputAddonBeforeDirective = /*#__PURE__*/(() => {
  class NzInputAddonBeforeDirective {
    static ɵfac = function NzInputAddonBeforeDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzInputAddonBeforeDirective)();
    };
    static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NzInputAddonBeforeDirective,
      selectors: [["", "nzInputAddonBefore", ""]]
    });
  }
  return NzInputAddonBeforeDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NzInputAddonAfterDirective = /*#__PURE__*/(() => {
  class NzInputAddonAfterDirective {
    static ɵfac = function NzInputAddonAfterDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzInputAddonAfterDirective)();
    };
    static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NzInputAddonAfterDirective,
      selectors: [["", "nzInputAddonAfter", ""]]
    });
  }
  return NzInputAddonAfterDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzInputPrefixDirective = /*#__PURE__*/(() => {
  class NzInputPrefixDirective {
    static ɵfac = function NzInputPrefixDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzInputPrefixDirective)();
    };
    static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NzInputPrefixDirective,
      selectors: [["", "nzInputPrefix", ""]]
    });
  }
  return NzInputPrefixDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NzInputSuffixDirective = /*#__PURE__*/(() => {
  class NzInputSuffixDirective {
    static ɵfac = function NzInputSuffixDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzInputSuffixDirective)();
    };
    static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NzInputSuffixDirective,
      selectors: [["", "nzInputSuffix", ""]]
    });
  }
  return NzInputSuffixDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzInputGroupSlotComponent = /*#__PURE__*/(() => {
  class NzInputGroupSlotComponent {
    icon = null;
    type = null;
    template = null;
    static ɵfac = function NzInputGroupSlotComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzInputGroupSlotComponent)();
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NzInputGroupSlotComponent,
      selectors: [["", "nz-input-group-slot", ""]],
      hostVars: 6,
      hostBindings: function NzInputGroupSlotComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵclassProp("ant-input-group-addon", ctx.type === "addon")("ant-input-prefix", ctx.type === "prefix")("ant-input-suffix", ctx.type === "suffix");
        }
      },
      inputs: {
        icon: "icon",
        type: "type",
        template: "template"
      },
      attrs: _c0,
      ngContentSelectors: _c1,
      decls: 3,
      vars: 2,
      consts: [[3, "nzType"], [4, "nzStringTemplateOutlet"]],
      template: function NzInputGroupSlotComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵconditionalCreate(0, NzInputGroupSlotComponent_Conditional_0_Template, 1, 1, "nz-icon", 0);
          i0.ɵɵtemplate(1, NzInputGroupSlotComponent_ng_container_1_Template, 2, 1, "ng-container", 1);
          i0.ɵɵprojection(2);
        }
        if (rf & 2) {
          i0.ɵɵconditional(ctx.icon ? 0 : -1);
          i0.ɵɵadvance();
          i0.ɵɵproperty("nzStringTemplateOutlet", ctx.template);
        }
      },
      dependencies: [NzIconModule, i1.NzIconDirective, NzOutletModule, i2.NzStringTemplateOutletDirective],
      encapsulation: 2,
      changeDetection: 0
    });
  }
  return NzInputGroupSlotComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzInputDirective = /*#__PURE__*/(() => {
  class NzInputDirective {
    renderer = inject(Renderer2);
    elementRef = inject(ElementRef);
    hostView = inject(ViewContainerRef);
    directionality = inject(Directionality);
    compactSize = inject(NZ_SPACE_COMPACT_SIZE, {
      optional: true
    });
    destroyRef = inject(DestroyRef);
    nzFormStatusService = inject(NzFormStatusService, {
      optional: true
    });
    nzFormNoStatusService = inject(NzFormNoStatusService, {
      optional: true
    });
    focusMonitor = inject(FocusMonitor);
    /**
     * @deprecated Will be removed in v21. It is recommended to use `nzVariant` instead.
     */
    nzBorderless = false;
    nzVariant = 'outlined';
    nzSize = 'default';
    nzStepperless = true;
    nzStatus = '';
    get disabled() {
      if (this.ngControl && this.ngControl.disabled !== null) {
        return this.ngControl.disabled;
      }
      return this._disabled;
    }
    set disabled(value) {
      this._disabled = value;
    }
    _disabled = false;
    disabled$ = new Subject();
    dir = 'ltr';
    // status
    prefixCls = 'ant-input';
    status = '';
    statusCls = {};
    hasFeedback = false;
    feedbackRef = null;
    components = [];
    ngControl = inject(NgControl, {
      self: true,
      optional: true
    });
    focused = signal(false);
    finalSize = computed(() => {
      if (this.compactSize) {
        return this.compactSize();
      }
      return this.size();
    });
    size = signal(this.nzSize);
    constructor() {
      this.destroyRef.onDestroy(() => {
        this.focusMonitor.stopMonitoring(this.elementRef);
      });
    }
    ngOnInit() {
      this.nzFormStatusService?.formStatusChanges.pipe(distinctUntilChanged((pre, cur) => {
        return pre.status === cur.status && pre.hasFeedback === cur.hasFeedback;
      }), takeUntilDestroyed(this.destroyRef)).subscribe(({
        status,
        hasFeedback
      }) => {
        this.setStatusStyles(status, hasFeedback);
      });
      if (this.ngControl) {
        this.ngControl.statusChanges?.pipe(filter(() => this.ngControl.disabled !== null), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this.disabled$.next(this.ngControl.disabled);
        });
      }
      this.dir = this.directionality.value;
      this.directionality.change?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(direction => {
        this.dir = direction;
      });
      this.focusMonitor.monitor(this.elementRef, false).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(origin => this.focused.set(!!origin));
    }
    ngOnChanges({
      disabled,
      nzStatus,
      nzSize
    }) {
      if (disabled) {
        this.disabled$.next(this.disabled);
      }
      if (nzStatus) {
        this.setStatusStyles(this.nzStatus, this.hasFeedback);
      }
      if (nzSize) {
        this.size.set(nzSize.currentValue);
      }
    }
    setStatusStyles(status, hasFeedback) {
      // set inner status
      this.status = status;
      this.hasFeedback = hasFeedback;
      this.renderFeedbackIcon();
      // render status if nzStatus is set
      this.statusCls = getStatusClassNames(this.prefixCls, status, hasFeedback);
      Object.keys(this.statusCls).forEach(status => {
        if (this.statusCls[status]) {
          this.renderer.addClass(this.elementRef.nativeElement, status);
        } else {
          this.renderer.removeClass(this.elementRef.nativeElement, status);
        }
      });
    }
    renderFeedbackIcon() {
      if (!this.status || !this.hasFeedback || !!this.nzFormNoStatusService) {
        // remove feedback
        this.hostView.clear();
        this.feedbackRef = null;
        return;
      }
      this.feedbackRef = this.feedbackRef || this.hostView.createComponent(NzFormItemFeedbackIconComponent);
      this.feedbackRef.location.nativeElement.classList.add('ant-input-suffix');
      this.feedbackRef.instance.status = this.status;
      this.feedbackRef.instance.updateIcon();
    }
    static ɵfac = function NzInputDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzInputDirective)();
    };
    static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NzInputDirective,
      selectors: [["input", "nz-input", ""], ["textarea", "nz-input", ""]],
      hostAttrs: [1, "ant-input"],
      hostVars: 19,
      hostBindings: function NzInputDirective_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵattribute("disabled", ctx.disabled || null);
          i0.ɵɵclassProp("ant-input-disabled", ctx.disabled)("ant-input-borderless", ctx.nzVariant === "borderless" || ctx.nzVariant === "outlined" && ctx.nzBorderless)("ant-input-filled", ctx.nzVariant === "filled")("ant-input-underlined", ctx.nzVariant === "underlined")("ant-input-lg", ctx.finalSize() === "large")("ant-input-sm", ctx.finalSize() === "small")("ant-input-rtl", ctx.dir === "rtl")("ant-input-stepperless", ctx.nzStepperless)("ant-input-focused", ctx.focused());
        }
      },
      inputs: {
        nzBorderless: [2, "nzBorderless", "nzBorderless", booleanAttribute],
        nzVariant: "nzVariant",
        nzSize: "nzSize",
        nzStepperless: [2, "nzStepperless", "nzStepperless", booleanAttribute],
        nzStatus: "nzStatus",
        disabled: [2, "disabled", "disabled", booleanAttribute]
      },
      exportAs: ["nzInput"],
      features: [i0.ɵɵProvidersFeature([{
        provide: NZ_SPACE_COMPACT_ITEM_TYPE,
        useValue: 'input'
      }]), i0.ɵɵHostDirectivesFeature([i1$1.NzSpaceCompactItemDirective]), i0.ɵɵNgOnChangesFeature]
    });
  }
  return NzInputDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzInputGroupWhitSuffixOrPrefixDirective = /*#__PURE__*/(() => {
  class NzInputGroupWhitSuffixOrPrefixDirective {
    elementRef = inject(ElementRef);
    static ɵfac = function NzInputGroupWhitSuffixOrPrefixDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzInputGroupWhitSuffixOrPrefixDirective)();
    };
    static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NzInputGroupWhitSuffixOrPrefixDirective,
      selectors: [["nz-input-group", "nzSuffix", ""], ["nz-input-group", "nzPrefix", ""]]
    });
  }
  return NzInputGroupWhitSuffixOrPrefixDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NzInputGroupComponent = /*#__PURE__*/(() => {
  class NzInputGroupComponent {
    focusMonitor = inject(FocusMonitor);
    elementRef = inject(ElementRef);
    renderer = inject(Renderer2);
    cdr = inject(ChangeDetectorRef);
    directionality = inject(Directionality);
    destroyRef = inject(DestroyRef);
    nzFormStatusService = inject(NzFormStatusService, {
      optional: true
    });
    nzFormNoStatusService = inject(NzFormNoStatusService, {
      optional: true
    });
    listOfNzInputDirective;
    nzAddOnBeforeIcon = null;
    nzAddOnAfterIcon = null;
    nzPrefixIcon = null;
    nzSuffixIcon = null;
    nzAddOnBefore;
    nzAddOnAfter;
    nzPrefix;
    nzStatus = '';
    nzSuffix;
    nzSize = 'default';
    nzSearch = false;
    isLarge = false;
    isSmall = false;
    isAffix = false;
    isAddOn = false;
    isFeedback = false;
    focused = false;
    disabled = false;
    dir = 'ltr';
    // status
    prefixCls = 'ant-input';
    affixStatusCls = {};
    groupStatusCls = {};
    affixInGroupStatusCls = {};
    status = '';
    hasFeedback = false;
    constructor() {
      this.destroyRef.onDestroy(() => this.focusMonitor.stopMonitoring(this.elementRef));
    }
    updateChildrenInputSize() {
      if (this.listOfNzInputDirective) {
        this.listOfNzInputDirective.forEach(item => item['size'].set(this.nzSize));
      }
    }
    ngOnInit() {
      this.nzFormStatusService?.formStatusChanges.pipe(distinctUntilChanged((pre, cur) => pre.status === cur.status && pre.hasFeedback === cur.hasFeedback), takeUntilDestroyed(this.destroyRef)).subscribe(({
        status,
        hasFeedback
      }) => {
        this.setStatusStyles(status, hasFeedback);
      });
      this.focusMonitor.monitor(this.elementRef, true).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(focusOrigin => {
        this.focused = !!focusOrigin;
        this.cdr.markForCheck();
      });
      this.dir = this.directionality.value;
      this.directionality.change?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(direction => {
        this.dir = direction;
      });
    }
    ngAfterContentInit() {
      this.updateChildrenInputSize();
      const listOfInputChange$ = this.listOfNzInputDirective.changes.pipe(startWith(this.listOfNzInputDirective));
      listOfInputChange$.pipe(switchMap(list => merge(...[listOfInputChange$, ...list.map(input => input.disabled$)])), mergeMap(() => listOfInputChange$), map(list => list.some(input => input.disabled)), takeUntilDestroyed(this.destroyRef)).subscribe(disabled => {
        this.disabled = disabled;
        this.cdr.markForCheck();
      });
    }
    ngOnChanges(changes) {
      const {
        nzSize,
        nzSuffix,
        nzPrefix,
        nzPrefixIcon,
        nzSuffixIcon,
        nzAddOnAfter,
        nzAddOnBefore,
        nzAddOnAfterIcon,
        nzAddOnBeforeIcon,
        nzStatus
      } = changes;
      if (nzSize) {
        this.updateChildrenInputSize();
        this.isLarge = this.nzSize === 'large';
        this.isSmall = this.nzSize === 'small';
      }
      if (nzSuffix || nzPrefix || nzPrefixIcon || nzSuffixIcon) {
        this.isAffix = !!(this.nzSuffix || this.nzPrefix || this.nzPrefixIcon || this.nzSuffixIcon);
      }
      if (nzAddOnAfter || nzAddOnBefore || nzAddOnAfterIcon || nzAddOnBeforeIcon) {
        this.isAddOn = !!(this.nzAddOnAfter || this.nzAddOnBefore || this.nzAddOnAfterIcon || this.nzAddOnBeforeIcon);
        this.nzFormNoStatusService?.noFormStatus?.next(this.isAddOn);
      }
      if (nzStatus) {
        this.setStatusStyles(this.nzStatus, this.hasFeedback);
      }
    }
    setStatusStyles(status, hasFeedback) {
      // set inner status
      this.status = status;
      this.hasFeedback = hasFeedback;
      this.isFeedback = !!status && hasFeedback;
      const baseAffix = !!(this.nzSuffix || this.nzPrefix || this.nzPrefixIcon || this.nzSuffixIcon);
      this.isAffix = baseAffix || !this.isAddOn && hasFeedback;
      this.affixInGroupStatusCls = this.isAffix || this.isFeedback ? this.affixStatusCls = getStatusClassNames(`${this.prefixCls}-affix-wrapper`, status, hasFeedback) : {};
      this.cdr.markForCheck();
      // render status if nzStatus is set
      this.affixStatusCls = getStatusClassNames(`${this.prefixCls}-affix-wrapper`, this.isAddOn ? '' : status, this.isAddOn ? false : hasFeedback);
      this.groupStatusCls = getStatusClassNames(`${this.prefixCls}-group-wrapper`, this.isAddOn ? status : '', this.isAddOn ? hasFeedback : false);
      const statusCls = {
        ...this.affixStatusCls,
        ...this.groupStatusCls
      };
      Object.keys(statusCls).forEach(status => {
        if (statusCls[status]) {
          this.renderer.addClass(this.elementRef.nativeElement, status);
        } else {
          this.renderer.removeClass(this.elementRef.nativeElement, status);
        }
      });
    }
    static ɵfac = function NzInputGroupComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzInputGroupComponent)();
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NzInputGroupComponent,
      selectors: [["nz-input-group"]],
      contentQueries: function NzInputGroupComponent_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuery(dirIndex, NzInputDirective, 4);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.listOfNzInputDirective = _t);
        }
      },
      hostVars: 38,
      hostBindings: function NzInputGroupComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵclassProp("ant-input-search-enter-button", ctx.nzSearch)("ant-input-search", ctx.nzSearch)("ant-input-search-rtl", ctx.dir === "rtl")("ant-input-search-sm", ctx.nzSearch && ctx.isSmall)("ant-input-search-large", ctx.nzSearch && ctx.isLarge)("ant-input-group-wrapper", ctx.isAddOn)("ant-input-group-wrapper-rtl", ctx.dir === "rtl")("ant-input-group-wrapper-lg", ctx.isAddOn && ctx.isLarge)("ant-input-group-wrapper-sm", ctx.isAddOn && ctx.isSmall)("ant-input-affix-wrapper", ctx.isAffix && !ctx.isAddOn)("ant-input-affix-wrapper-rtl", ctx.dir === "rtl")("ant-input-affix-wrapper-focused", ctx.isAffix && ctx.focused)("ant-input-affix-wrapper-disabled", ctx.isAffix && ctx.disabled)("ant-input-affix-wrapper-lg", ctx.isAffix && !ctx.isAddOn && ctx.isLarge)("ant-input-affix-wrapper-sm", ctx.isAffix && !ctx.isAddOn && ctx.isSmall)("ant-input-group", !ctx.isAffix && !ctx.isAddOn)("ant-input-group-rtl", ctx.dir === "rtl")("ant-input-group-lg", !ctx.isAffix && !ctx.isAddOn && ctx.isLarge)("ant-input-group-sm", !ctx.isAffix && !ctx.isAddOn && ctx.isSmall);
        }
      },
      inputs: {
        nzAddOnBeforeIcon: "nzAddOnBeforeIcon",
        nzAddOnAfterIcon: "nzAddOnAfterIcon",
        nzPrefixIcon: "nzPrefixIcon",
        nzSuffixIcon: "nzSuffixIcon",
        nzAddOnBefore: "nzAddOnBefore",
        nzAddOnAfter: "nzAddOnAfter",
        nzPrefix: "nzPrefix",
        nzStatus: "nzStatus",
        nzSuffix: "nzSuffix",
        nzSize: "nzSize",
        nzSearch: [2, "nzSearch", "nzSearch", booleanAttribute]
      },
      exportAs: ["nzInputGroup"],
      features: [i0.ɵɵProvidersFeature([NzFormNoStatusService, {
        provide: NZ_SPACE_COMPACT_ITEM_TYPE,
        useValue: 'input'
      }]), i0.ɵɵHostDirectivesFeature([i1$1.NzSpaceCompactItemDirective]), i0.ɵɵNgOnChangesFeature],
      ngContentSelectors: _c1,
      decls: 6,
      vars: 1,
      consts: [["affixTemplate", ""], ["contentTemplate", ""], [1, "ant-input-wrapper", "ant-input-group"], ["nz-input-group-slot", "", "type", "addon", 3, "icon", "template"], [1, "ant-input-affix-wrapper", 3, "ant-input-affix-wrapper-disabled", "ant-input-affix-wrapper-sm", "ant-input-affix-wrapper-lg", "ant-input-affix-wrapper-focused", "class"], [3, "ngTemplateOutlet"], [1, "ant-input-affix-wrapper"], ["nz-input-group-slot", "", "type", "prefix", 3, "icon", "template"], ["nz-input-group-slot", "", "type", "suffix", 3, "icon", "template"], [3, "status"], ["nz-input-group-slot", "", "type", "suffix"]],
      template: function NzInputGroupComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵconditionalCreate(0, NzInputGroupComponent_Conditional_0_Template, 5, 3, "span", 2)(1, NzInputGroupComponent_Conditional_1_Template, 2, 1);
          i0.ɵɵtemplate(2, NzInputGroupComponent_ng_template_2_Template, 3, 3, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor)(4, NzInputGroupComponent_ng_template_4_Template, 2, 1, "ng-template", null, 1, i0.ɵɵtemplateRefExtractor);
        }
        if (rf & 2) {
          i0.ɵɵconditional(ctx.isAddOn ? 0 : 1);
        }
      },
      dependencies: [NzInputGroupSlotComponent, NgTemplateOutlet, NzFormItemFeedbackIconComponent],
      encapsulation: 2,
      changeDetection: 0
    });
  }
  return NzInputGroupComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzInputOtpComponent = /*#__PURE__*/(() => {
  class NzInputOtpComponent {
    formBuilder = inject(FormBuilder);
    destroyRef = inject(DestroyRef);
    otpInputs;
    nzLength = 6;
    nzSize = 'default';
    disabled = false;
    nzStatus = '';
    nzFormatter = value => value;
    nzMask = null;
    otpArray;
    internalValue = [];
    onChangeCallback;
    onTouched = () => {};
    constructor() {
      this.createFormArray();
    }
    ngOnChanges(changes) {
      if (changes['nzLength']?.currentValue) {
        this.createFormArray();
      }
      if (changes['disabled']) {
        this.setDisabledState(this.disabled);
      }
    }
    onInput(index, event) {
      const inputElement = event.target;
      const nextInput = this.otpInputs.toArray()[index + 1];
      if (inputElement.value && nextInput) {
        nextInput.nativeElement.focus();
      } else if (!nextInput) {
        this.selectInputBox(index);
      }
    }
    onFocus(event) {
      const inputElement = event.target;
      inputElement.select();
    }
    onKeyDown(index, event) {
      const previousInput = this.otpInputs.toArray()[index - 1];
      if (event.keyCode === BACKSPACE) {
        event.preventDefault();
        this.internalValue[index] = '';
        this.otpArray.at(index).setValue('', {
          emitEvent: false
        });
        if (previousInput) {
          this.selectInputBox(index - 1);
        }
        this.emitValue();
      }
    }
    writeValue(value) {
      if (!value) {
        this.otpArray.reset();
        return;
      }
      const controlValues = value.split('');
      this.internalValue = controlValues;
      controlValues.forEach((val, i) => {
        const formattedValue = this.nzFormatter(val);
        const value = this.nzMask ? this.nzMask : formattedValue;
        this.otpArray.at(i).setValue(value, {
          emitEvent: false
        });
      });
    }
    registerOnChange(fn) {
      this.onChangeCallback = fn;
    }
    registerOnTouched(fn) {
      this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
      if (isDisabled) {
        this.otpArray.disable();
      } else {
        this.otpArray.enable();
      }
    }
    onPaste(index, event) {
      const pastedText = event.clipboardData?.getData('text') || '';
      if (!pastedText) return;
      let currentIndex = index;
      for (const char of pastedText.split('')) {
        if (currentIndex < this.nzLength) {
          const formattedChar = this.nzFormatter(char);
          this.internalValue[currentIndex] = char;
          const maskedValue = this.nzMask ? this.nzMask : formattedChar;
          this.otpArray.at(currentIndex).setValue(maskedValue, {
            emitEvent: false
          });
          currentIndex++;
        } else {
          break;
        }
      }
      event.preventDefault(); // this line is needed, otherwise the last index that is going to be selected will also be filled (in the next line).
      this.selectInputBox(currentIndex);
      this.emitValue();
    }
    createFormArray() {
      this.otpArray = this.formBuilder.array([]);
      this.internalValue = new Array(this.nzLength).fill('');
      for (let i = 0; i < this.nzLength; i++) {
        const control = this.formBuilder.nonNullable.control('', [Validators.required]);
        control.valueChanges.pipe(tap(value => {
          const unmaskedValue = this.nzFormatter(value);
          this.internalValue[i] = unmaskedValue;
          control.setValue(this.nzMask ?? unmaskedValue, {
            emitEvent: false
          });
          this.emitValue();
        }), takeUntilDestroyed(this.destroyRef)).subscribe();
        this.otpArray.push(control);
      }
    }
    emitValue() {
      const result = this.internalValue.join('');
      if (this.onChangeCallback) {
        this.onChangeCallback(result);
      }
    }
    selectInputBox(index) {
      const otpInputArray = this.otpInputs.toArray();
      if (index >= otpInputArray.length) index = otpInputArray.length - 1;
      otpInputArray[index].nativeElement.select();
    }
    static ɵfac = function NzInputOtpComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzInputOtpComponent)();
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NzInputOtpComponent,
      selectors: [["nz-input-otp"]],
      viewQuery: function NzInputOtpComponent_Query(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵviewQuery(_c2, 5);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.otpInputs = _t);
        }
      },
      hostAttrs: [1, "ant-otp"],
      inputs: {
        nzLength: [2, "nzLength", "nzLength", numberAttribute],
        nzSize: "nzSize",
        disabled: [2, "disabled", "disabled", booleanAttribute],
        nzStatus: "nzStatus",
        nzFormatter: "nzFormatter",
        nzMask: "nzMask"
      },
      exportAs: ["nzInputOtp"],
      features: [i0.ɵɵProvidersFeature([{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => NzInputOtpComponent),
        multi: true
      }]), i0.ɵɵNgOnChangesFeature],
      decls: 2,
      vars: 0,
      consts: [["otpInput", ""], ["nz-input", "", "type", "text", "maxlength", "1", "size", "1", 1, "ant-otp-input", 3, "nzSize", "formControl", "nzStatus"], ["nz-input", "", "type", "text", "maxlength", "1", "size", "1", 1, "ant-otp-input", 3, "input", "focus", "keydown", "paste", "nzSize", "formControl", "nzStatus"]],
      template: function NzInputOtpComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵrepeaterCreate(0, NzInputOtpComponent_For_1_Template, 2, 3, "input", 1, i0.ɵɵrepeaterTrackByIndex);
        }
        if (rf & 2) {
          i0.ɵɵrepeater(ctx.otpArray.controls);
        }
      },
      dependencies: [NzInputDirective, ReactiveFormsModule, i1$2.DefaultValueAccessor, i1$2.NgControlStatus, i1$2.MaxLengthValidator, i1$2.FormControlDirective],
      encapsulation: 2,
      changeDetection: 0
    });
  }
  return NzInputOtpComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzTextareaCountComponent = /*#__PURE__*/(() => {
  class NzTextareaCountComponent {
    renderer = inject(Renderer2);
    destroyRef = inject(DestroyRef);
    elementRef = inject(ElementRef);
    nzInputDirective;
    nzMaxCharacterCount = 0;
    nzComputeCharacterCount = v => v.length;
    nzFormatter = (c, m) => `${c}${m > 0 ? `/${m}` : ``}`;
    ngAfterContentInit() {
      if (!this.nzInputDirective && isDevMode()) {
        throw new Error('[nz-textarea-count]: Could not find matching textarea[nz-input] child.');
      }
      if (this.nzInputDirective.ngControl) {
        const valueChanges = this.nzInputDirective.ngControl.valueChanges || EMPTY;
        valueChanges.pipe(takeUntilDestroyed(this.destroyRef), map(() => this.nzInputDirective.ngControl.value), startWith(this.nzInputDirective.ngControl.value)).subscribe(value => {
          this.setDataCount(value);
        });
      }
    }
    setDataCount(value) {
      const inputValue = isNotNil(value) ? String(value) : '';
      const currentCount = this.nzComputeCharacterCount(inputValue);
      const dataCount = this.nzFormatter(currentCount, this.nzMaxCharacterCount);
      this.renderer.setAttribute(this.elementRef.nativeElement, 'data-count', dataCount);
    }
    static ɵfac = function NzTextareaCountComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzTextareaCountComponent)();
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NzTextareaCountComponent,
      selectors: [["nz-textarea-count"]],
      contentQueries: function NzTextareaCountComponent_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuery(dirIndex, NzInputDirective, 7);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.nzInputDirective = _t.first);
        }
      },
      hostAttrs: [1, "ant-input-textarea-show-count"],
      inputs: {
        nzMaxCharacterCount: [2, "nzMaxCharacterCount", "nzMaxCharacterCount", numberAttribute],
        nzComputeCharacterCount: "nzComputeCharacterCount",
        nzFormatter: "nzFormatter"
      },
      ngContentSelectors: _c4,
      decls: 1,
      vars: 0,
      template: function NzTextareaCountComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef(_c3);
          i0.ɵɵprojection(0);
        }
      },
      encapsulation: 2,
      changeDetection: 0
    });
  }
  return NzTextareaCountComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzInputModule = /*#__PURE__*/(() => {
  class NzInputModule {
    static ɵfac = function NzInputModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzInputModule)();
    };
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: NzInputModule
    });
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [NzInputGroupComponent, NzInputGroupSlotComponent, NzInputOtpComponent]
    });
  }
  return NzInputModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NzAutosizeDirective, NzInputAddonAfterDirective, NzInputAddonBeforeDirective, NzInputDirective, NzInputGroupComponent, NzInputGroupSlotComponent, NzInputGroupWhitSuffixOrPrefixDirective, NzInputModule, NzInputOtpComponent, NzInputPrefixDirective, NzInputSuffixDirective, NzTextareaCountComponent };
